import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { getUuid } from '@front10/helpers/dist/uuid';
import useStyles from '@front10/helpers/dist/UseStyles';
import { Context } from '@front10/helpers/dist/Context';

import Button from '../../elements/Button';
import Icon from '../../elements/Icon';

const LoadMorePaginator = (props) => {
  const id = getUuid();
  const {
    labelKey,
    labelLoadingKey,
    fullWidth,
    showLoadingIcon,
    onClickAction,
    isLoading,
    disabled,
    buttonSetting,
    iconSetting,
    focusReferences,
    showIcon,
    icon,
    iconStyles
  } = props;
  const { translate, direction, theme } = useContext(Context);
  const [className, style] = useStyles(props, { direction, theme });
  const styles = { ...style };
  styles.width = fullWidth ? '100%' : 'auto';

  const handleKeyDown = ($event) => {
    if ($event.keyCode === 13 || $event.keyCode === 32) {
      onClickAction($event, true);
    }
  };

  return (
    <>
      <Button
        focusReferences={focusReferences}
        disabled={isLoading || disabled}
        onClick={onClickAction}
        onKeyDown={handleKeyDown}
        className={className}
        {...buttonSetting}
        style={styles}
        ariaLabel={translate(
          'cmp-load-more-paginator-aria-description',
          'Load more items on the list'
        )}
        ariaDescribedBy={`${id}`}
        dataTestId="loadMoreButton"
      >
        {!isLoading ? (
          <>
            {translate(labelKey, 'Load More')}
            {showIcon && <Icon icon={icon} {...iconStyles} />}
          </>
        ) : (
          <>
            {translate(labelLoadingKey, 'Loading')}
            {showLoadingIcon && (
              <Icon {...iconSetting} display="inline-block" align="middle" icon="spinner" />
            )}
          </>
        )}
      </Button>
      <p className="fr-sr-only" id={id}>
        {translate(
          'cmp-load-more-aria-description',
          'Press the Enter/Space key to load more items on the list or press the TAB key to continue'
        )}
      </p>
    </>
  );
};

LoadMorePaginator.propTypes = {
  /**
   * button Setting props
   */
  buttonSetting: PropTypes.objectOf(PropTypes.any),
  /**
   * icon settings
   */
  iconSetting: PropTypes.objectOf(PropTypes.any),
  /**
   * Translation key
   */
  labelKey: PropTypes.string,
  /**
   * Translation key of the loading text
   */
  labelLoadingKey: PropTypes.string,
  /**
   * View in full width
   */
  fullWidth: PropTypes.bool,
  /**
   * Show Loading Icon
   */
  showLoadingIcon: PropTypes.bool,
  /**
   * Determine if the component is disabled
   */
  disabled: PropTypes.bool,
  /**
   * Determine if show loading
   */
  isLoading: PropTypes.bool,
  /**
   * Function fired when click
   */
  onClickAction: PropTypes.func,
  /**
   * ref focus
   */
  focusReferences: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  /**
   * show icon
   */
  showIcon: PropTypes.bool,
  /**
   * icon to show
   */
  icon: PropTypes.string,
  /**
   * icon Setting props
   */
  iconStyles: PropTypes.objectOf(PropTypes.any)
};

LoadMorePaginator.defaultProps = {
  buttonSetting: {},
  iconSetting: {
    color: 'black'
  },
  labelKey: 'load_more_pagination_label',
  labelLoadingKey: 'load_more_pagination_loading_label',
  fullWidth: false,
  showLoadingIcon: true,
  isLoading: false,
  disabled: false,
  showIcon: false,
  iconStyles: {},
  icon: '',
  onClickAction: () => {},
  focusReferences: () => {}
};

LoadMorePaginator.componentName = 'LoadMorePaginator';

export default LoadMorePaginator;
